// src/components/MyPrototypesPage.js
import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  TextField,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import Header from './Header.js';
import SettingsDialog from './SettingsDialog.js';
import usePrototypes from '../hooks/usePrototypes.js';
import PrototypeCard from './PrototypeCard.js';

export default function MyPrototypesPage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = usePrototypes();

  const [list, setList] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') || '';
  const [searchTerm, setSearchTerm] = useState(search);

  // Sync fetched data
  useEffect(() => {
    if (!loading && !error) {
      setList(data);
    }
  }, [data, loading, error]);

  // Delete handler
  const handleConfirmDelete = async () => {
    try {
      await fetch(`/api/prototypes/${deleteId}`, { method: 'DELETE' });
      setList(prev => prev.filter(p => p.chatId !== deleteId));
    } catch (e) {
      console.error('Failed to delete prototype', e);
    } finally {
      setDeleteId(null);
    }
  };

  // Filter list by search
  const filteredList = list.filter(proto => {
    const q = searchTerm.toLowerCase();
    return (
      proto.title?.toLowerCase().includes(q) ||
      proto.user_prompt?.toLowerCase().includes(q) ||
      proto.ai_description?.toLowerCase().includes(q)
    );
  });

  const handleSearchChange = e => {
    const v = e.target.value;
    setSearchTerm(v);
    setSearchParams(v ? { search: v } : {});
  };

  if (loading) return <div style={{ padding: 32 }}>Loading…</div>;
  if (error) return <div style={{ padding: 32, color: 'red' }}>Error loading prototypes.</div>;

  return (
    <>
      <Header
        viewMode="gallery"
        toggleView={() => {}}
        history={[]}
        timestamps={[]}
        historyIndex={0}
        jumpToVersion={() => {}}
        onSettingsClick={() => setSettingsOpen(true)}
      />

      <div style={{ padding: 32 }}>
        <div className="gallery-header" style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, flexGrow: 1 }}>My Gallery ({filteredList.length})</h2>
          <TextField
            size="small"
            placeholder="Search prototypes…"
            value={searchTerm}
            onChange={handleSearchChange}
            variant="outlined"
            sx={{ minWidth: 200 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchTerm('');
                      setSearchParams({});
                    }}
                    edge="end"
                    style={{ visibility: searchTerm ? 'visible' : 'hidden' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </div>

        <div
          style={{
            display: 'grid',
            gap: 24,
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            alignItems: 'start',
          }}
        >
          {/* "Create New" placeholder */}
          <Link
            to={`/${username}`}
            style={{
              textDecoration: 'none',
              color: 'inherit',
              display: 'block',
              border: '2px dashed #ccc',
              borderRadius: 6,
              overflow: 'hidden',
              transition: 'box-shadow .15s',
            }}
          >
            <div
              style={{
                height: 200,
                background: '#f3f3f3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span style={{ fontSize: 48, color: '#bbb' }}>＋</span>
            </div>
            <div style={{ padding: '30px 0', textAlign: 'center' }}>
              <span style={{ fontWeight: 600, fontSize: 15 }}>Create New</span>
            </div>
          </Link>

          {/* Render each prototype with actions */}
          {filteredList.map(proto => (
            <PrototypeCard
              key={proto.chatId}
              proto={proto}
              onDelete={id => setDeleteId(id)}
              onEdit={id => navigate(`/${username}/${id}`, { state: { sidebarVisible: true } })}
            />
          ))}
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Prototype?</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this Prototype? This action cannot be reversed.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete}>
            DELETE
          </Button>
        </DialogActions>
      </Dialog>

      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
}
